'use client';

import { useEffect, useMemo } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { SolidNavbar } from '@/components/SolidNavbar';
import { Footer } from '@/components/Footer';
import { PAGE_CONTAINER } from '@/components/ui';
import { markdownToHtml } from '@/lib/markdown';
import type { BlogPostDetail, BlogPostListItem } from '@/lib/api';
import { isKnownImageHost } from '@/lib/image-hosts';
import { getEntryReferrerOrigin } from '@/lib/entry-referrer';
import { Clock, Calendar, ChevronRight, User } from 'lucide-react';

interface Props {
  post: BlogPostDetail;
  relatedPosts: BlogPostListItem[];
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '';
  // timeZone pinned to keep the SSR and hydration passes in agreement — see
  // BlogListClient.
  return new Date(dateStr).toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Asia/Jakarta',
  });
}

function JsonLd({ post }: { post: BlogPostDetail }) {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    image: post.coverImage?.url || post.ogImageUrl,
    author: {
      '@type': 'Person',
      name: post.author?.displayName || 'Aksademia',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Aksademia',
      url: 'https://aksademia.com',
    },
    datePublished: post.publishedAt,
    dateModified: post.updatedAt,
    description: post.metaDescription || post.excerpt,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
}

export function BlogPostDetailClient({ post, relatedPosts }: Props) {
  const t = useTranslations('blogPost');

  // Track view on mount (fire-and-forget)
  useEffect(() => {
    if (post?.slug) {
      // Origin only: the proxy does not forward the browser's Referer, so send it explicitly.
      fetch(`/api/proxy/blog/post/${post.slug}/view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ referrer: getEntryReferrerOrigin() }),
      }).catch(() => {});
    }
  }, [post?.slug]);

  const contentHtml = useMemo(() => {
    return post?.content ? markdownToHtml(post.content) : '';
  }, [post?.content]);

  const coverUrl = post.coverImage?.url || post.ogImageUrl || `/blog/${post.slug}/opengraph-image`;

  return (
    <>
      <JsonLd post={post} />
      <SolidNavbar
        breadcrumbs={[
          { label: 'Blog', href: '/blog' },
          ...(post.category ? [{ label: post.category.name, href: `/blog/category/${post.category.slug}` }] : []),
          { label: post.title, href: `/blog/${post.slug}` },
        ]}
      />
      <main className="bg-slate-50 pb-16">
        <div className={`${PAGE_CONTAINER} pt-6`}>
          {/* Article card. The reading column stays narrow (nothing else to put beside it); the related grid below uses the full width. */}
          <article className="max-w-4xl mx-auto bg-white rounded-xl border border-slate-200 overflow-hidden">
            {/* Cover Image — falls back to OG image when no dedicated cover. First
                thing rendered in the article, above the fold: the LCP candidate on
                this page, so it gets priority (never lazy). */}
            <div className="relative w-full h-52 sm:h-64 md:h-80">
              <Image
                src={coverUrl}
                alt={post.title}
                fill
                sizes="(max-width: 896px) 100vw, 896px"
                unoptimized={!isKnownImageHost(coverUrl)}
                priority
                className="object-cover"
              />
            </div>

            <div className="px-5 sm:px-8 py-6 sm:py-8">
              {/* Category */}
              {post.category && (
                <Link
                  href={`/blog/category/${post.category.slug}`}
                  className="inline-block text-[10px] font-semibold uppercase tracking-wide mb-3 px-2 py-0.5 rounded-md hover:opacity-80 transition-opacity"
                  style={{
                    backgroundColor: post.category.color ? `${post.category.color}15` : '#f0fdfa',
                    color: post.category.color || '#0f766e',
                  }}
                >
                  {post.category.name}
                </Link>
              )}

              {/* Title */}
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 leading-tight">
                {post.title}
              </h1>

              {/* Meta */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-6 pb-6 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-teal-100 flex items-center justify-center">
                    {post.author?.avatarUrl ? (
                      <Image
                        src={post.author.avatarUrl}
                        alt=""
                        width={28}
                        height={28}
                        unoptimized={!isKnownImageHost(post.author.avatarUrl)}
                        className="w-7 h-7 rounded-full"
                      />
                    ) : (
                      <User className="w-3.5 h-3.5 text-teal-600" />
                    )}
                  </div>
                  <span className="font-medium text-slate-700 text-sm">
                    {post.author?.displayName || 'Aksademia'}
                  </span>
                </div>
                <span className="flex items-center gap-1 text-sm text-slate-500">
                  <Calendar className="w-3.5 h-3.5" />
                  {formatDate(post.publishedAt)}
                </span>
                <span className="flex items-center gap-1 text-sm text-slate-500">
                  <Clock className="w-3.5 h-3.5" />
                  {t('minuteRead', { mins: post.readingTimeMins })}
                </span>
              </div>

              {/* Content */}
              <div
                className="prose-custom"
                dangerouslySetInnerHTML={{ __html: contentHtml }}
              />

              {/* Tags */}
              {post.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-8 pt-5 border-t border-slate-100">
                  {post.tags.map((tag) => (
                    <span
                      key={tag.id}
                      className="px-2.5 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-600"
                    >
                      {tag.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </article>

          {/* Other Posts */}
          {relatedPosts.length > 0 && (
            <section className="mt-8">
              <h2 className="text-lg font-bold text-slate-900 mb-4">{t('relatedPosts')}</h2>
              {/* Mobile: horizontal scroll strip; sm+: regular grid */}
              <div className="flex gap-4 overflow-x-auto pb-2 sm:pb-0 snap-x snap-mandatory sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:overflow-visible">
                {relatedPosts.map((related) => (
                  <Link
                    key={related.id}
                    href={`/blog/${related.slug}`}
                    className="group block flex-none w-64 sm:w-auto snap-start"
                  >
                    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-teal-300 hover:shadow-md transition-all h-full flex flex-col">
                      {(() => {
                        const relCover = related.coverImage?.url || related.ogImageUrl || `/blog/${related.slug}/opengraph-image`;
                        return relCover ? (
                          // Below the fold (bottom of article, after the full content
                          // body) — default lazy loading is correct, no priority.
                          <div className="relative aspect-[16/9] overflow-hidden">
                            <Image
                              src={relCover}
                              alt={related.title}
                              fill
                              sizes="(max-width: 640px) 256px, (max-width: 1024px) 50vw, 33vw"
                              unoptimized={!isKnownImageHost(relCover)}
                              className="object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                        ) : (
                          <div className="aspect-[16/9] bg-teal-50" />
                        );
                      })()}
                      <div className="p-4 flex-1">
                        <h3 className="font-semibold text-sm text-slate-900 group-hover:text-teal-700 transition-colors line-clamp-2 mb-2">
                          {related.title}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <span>{formatDate(related.publishedAt)}</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {related.readingTimeMins} {t('minShort')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
